// libraries
import React from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import Button from '/react4xp/components/Button'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils'

export type HeroButton = {
    text?: string
    link?: string
    externallink?: string
    outline?: boolean
}

export type HeroIconCta = {
    text?: string
    link?: string
    externallink?: string
    icon?: string // PNG or SVG path
}

export interface HeroProps {
    heading?: string
    view: 'wide-image' | 'image-right'
    image?: string
    buttons?: HeroButton[]
    icon_cta?: HeroIconCta[]
}

function resolveUrl(item: { link?: string; externallink?: string }): string | undefined {
    return item.externallink && isNonWhitespaceString(item.externallink)
        ? item.externallink
        : item.link
}

export default function Hero({
    heading,
    view = 'wide-image',
    image,
    buttons = [],
    icon_cta = []
}: HeroProps) {

    const bgColor = 'white'
    const safeButtons = (Array.isArray(buttons) ? buttons : []).filter((button): button is HeroButton =>
        isNonWhitespaceString(button?.text)
    )
    const safeIconCtas = (Array.isArray(icon_cta) ? icon_cta : []).filter((item) => {
        const hasText = isNonWhitespaceString(item?.text)
        const hasLink = isNonWhitespaceString(resolveUrl(item) || '')
        return hasText && hasLink
    })

    return (
        <Section bgColor={bgColor} className={`hero hero--${view}`}>
            <div className='hero__wrapper'>
                <div className='hero__container container'>
                    <div className='hero__content '>
                        {heading && isNonWhitespaceString(heading) && (
                            <h1 className='hero__heading'>{heading}</h1>
                        )}

                        {safeButtons.length > 0 && (
                            <div className='hero__buttons'>
                                {safeButtons.slice(0, 2).map((b, i) => {
                                    const label = b.text?.trim() ?? ''
                                    if (!label) {
                                        return null
                                    }
                                    return (
                                        <span key={i}>
                                            <Button
                                                href={resolveUrl(b) || '#'}
                                                style={b.outline ? 'hollow' : 'solid'}
                                                color='primary'
                                            >
                                                {label}
                                            </Button>
                                        </span>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    {image && (
                    <div className='hero__image hero__image--40'>
                        <img src={image} alt='' />
                    </div>
                )}
                </div>

                {image && (
                    <div className='hero__image hero__image--wide'>
                        <img src={image} alt='' />
                    </div>
                )}

            </div>

            {
                safeIconCtas.length > 0 && (
                    <div className='hero__icons'>
                        <div className='container'>
                            <div className={clsx('hero__icons-row', {
                                'hero__icons-row--two': safeIconCtas.slice(0, 3).length === 2,
                                'hero__icons-row--one': safeIconCtas.slice(0, 3).length === 1
                            })}>
                                {safeIconCtas.slice(0, 3).map((i, idx) => (
                                    <a key={idx} className='hero__icon' href={resolveUrl(i) || '#'}>
                                        <span className='hero__icon-text'>{i.text}</span>
                                        <span className='hero__icon-img'>
                                            {i.icon && (
                                                <img
                                                    src={i.icon}
                                                    alt=''
                                                    aria-hidden='true'
                                                />
                                            )}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }
        </Section >
    )
}